import utc from "dayjs/plugin/utc";
import tw from "dayjs/locale/zh-tw";
import dayjs, { OpUnitType, PluginFunc } from "dayjs";
import duration from "dayjs/plugin/duration";
import isBetween from "dayjs/plugin/isBetween";
import localizedFormat from "dayjs/plugin/localizedFormat";

declare module "dayjs" {
  interface Dayjs {
    asDate: () => string;
    asTime: () => string;
    asDateTime: () => string;
    asDateTimeSec: () => string;
    isStartOf: (unit: OpUnitType) => boolean;
    isEndOf: (unit: OpUnitType) => boolean;
  }

  /** 將 UTC 日期轉成本地`年-月-日`字串 */
  function $ld(t?: string): string;
  /** 將 UTC 日期轉成本地`時:分`字串 */
  function $lt(t?: string): string;
  /** 將 UTC 日期轉成本地`年-月-日 時:分`字串 */
  function $ldt(t?: string): string;
  /** 將 UTC 日期轉成本地`年-月-日 時:分:秒`字串 */
  function $ldts(t?: string): string;
}

const customFormat: PluginFunc = (
  _: unknown,
  dayjsClass: typeof dayjs.Dayjs,
  dayjsFactory: typeof dayjs
) => {
  dayjsClass.prototype.asDate = function () {
    return dayjsClass.prototype.format.bind(this)("YYYY-MM-DD");
  };

  dayjsClass.prototype.asTime = function () {
    return dayjsClass.prototype.format.bind(this)("HH:mm");
  };

  dayjsClass.prototype.asDateTime = function () {
    return dayjsClass.prototype.format.bind(this)("YYYY-MM-DD HH:mm");
  };

  dayjsClass.prototype.asDateTimeSec = function () {
    return dayjsClass.prototype.format.bind(this)("YYYY-MM-DD HH:mm:ss");
  };

  dayjsClass.prototype.isStartOf = function (unit: OpUnitType) {
    const endDateTime = dayjsClass.prototype.startOf.bind(this)(unit);
    return this.diff(endDateTime) === 0;
  };

  dayjsClass.prototype.isEndOf = function (unit: OpUnitType) {
    const endDateTime = dayjsClass.prototype.endOf.bind(this)(unit);
    return this.diff(endDateTime) === 0;
  };

  dayjsFactory.$lt = (t?: string) => {
    return t ? dayjs.utc(t).local().asTime() : "";
  };

  dayjsFactory.$ld = (t?: string) => {
    return t ? dayjs.utc(t).local().asDate() : "";
  };

  dayjsFactory.$ldt = (t?: string) => {
    return t ? dayjs.utc(t).local().asDateTime() : "";
  };

  dayjsFactory.$ldts = (t?: string) => {
    return t ? dayjs.utc(t).local().asDateTimeSec() : "";
  };
};

dayjs.locale({ ...tw, weekStart: 1 });
dayjs.extend(utc);
dayjs.extend(duration);
dayjs.extend(isBetween);
dayjs.extend(customFormat);
dayjs.extend(localizedFormat);
